import React, { useState, useContext, useEffect } from 'react';
import Cookies from 'js-cookie';
import { cartService, sparePartService } from '../services';
import { SparePart, SparePartCount, PartIdCount } from '../interfaces/Item';
import { CartRecipient } from '../interfaces/Cart';
import { wait } from '../helpers/time';
import { Nullable } from '../types/common';

type ItemId = number;
/**
 * Calculates the new number of items that can be added
 *
 * @param existing - Existing number of items
 * @param additive - How many items you're adding
 * @param limit - Limit
 *
 * @example
 * >calculateNewNumberOfItems(3, 13, 20)
 * 13
 * >calculateNewNumberOfItems(13, 13, 20)
 * 7
 */
export const calculateNewNumberOfItems = (
  existing: number,
  additive: number,
  limit: number
): number => {
  return Math.max(Math.min(limit - existing, additive), 0);
};
/**
 * Calculates the new number of items that's being able to added to the cart. Uses `calculateNewNumberOfItems` but has an override if undo is set
 *
 * @param existing - Existing number of items
 * @param additive - How many items you're adding
 * @param limit - Limit
 * @param undo - Overrides the behaviour to a `Math.min` of additive and limit
 *
 * @example
 * >calculateNewNumberOfItemsWithUndo(3, 13, 20, false)
 * 13
 * >calculateNewNumberOfItemsWithUndo(13, 13, 20, true)
 * 13
 * >calculateNewNumberOfItemsWithUndo(13, 13, 20, false)
 * 7
 * >calculateNewNumberOfItemsWithUndo(13, 13, 20, true)
 * 13
 */
export const calculateNewNumberOfItemsWithUndo = (
  existing: number,
  additive: number,
  limit: number,
  undo: boolean
): number => {
  return undo
    ? Math.min(additive, limit)
    : calculateNewNumberOfItems(existing, additive, limit);
};
/* globals CSS_CONFIG */
const { UPDATEPROFILECHECKBOX } = CSS_CONFIG;

interface CartState {
  items: Map<ItemId, SparePartCount>;
  recipient: CartRecipient;
  isLoading: boolean;
}

export const emptyRecipient: CartRecipient = {
  city: '',
  firstName: '',
  lastName: '',
  postCode: '',
  streetAddress: '',
  streetAddress2: '',
  streetAddress3: '',
  terms: false,
  email: '',
  phoneNumber: '',
  country: '',
  state: '',
  reason: '',
  updateProfileCheckbox: UPDATEPROFILECHECKBOX,
};
export interface Cart {
  state: CartState;
  getItems: () => SparePartCount[];
  getItemCount: (id: ItemId) => number;
  editItem(item: SparePart, numberOfItems: number): Promise<void>;
  addItem(
    item: SparePart,
    numberOfItems?: number,
    undo?: boolean
  ): Promise<void>;
  removeItem(item: SparePart): Promise<void>;
  setRecipient: (recipient: CartRecipient) => void;
  createOrder: (recipient: CartRecipient) => Promise<void>;
  getTotalNrOfItems: () => number;
  turnstileToken: string;
}

const CartContext = React.createContext<Nullable<Cart>>(null);

interface CartError {
  status: number;
  data: {
    message: {
      serviceError: string;
    };
  };
}
// "/xx/xx/customer-service/returns-claims/spareparts/".length === 50
const cookiePath =
  typeof window !== 'undefined'
    ? window.location.pathname.substring(0, 50)
    : '';
const CartProvider: React.FC = ({ children }): JSX.Element => {
  const [sparePartCounts, setSparePartCounts] = useState<
    Map<ItemId, SparePartCount>
  >(new Map());
  const [recipient, setRecipient] = useState<CartRecipient>(emptyRecipient);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // TODO: Get cartId from logged in user
  const [cartId, setCartId] = useState<string | null>(Cookies.get('cartId'));

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  useEffect(() => {
    const handleToken = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      setTurnstileToken(customEvent.detail);
    };
    window.addEventListener('turnstile-token', handleToken);
    return () => {
      window.removeEventListener('turnstile-token', handleToken);
    };
  }, []);

  const cartDoesNotExist = (e: CartError): boolean =>
    e.status === 404 && e.data.message.serviceError === 'CART_DOES_NOT_EXIST';

  const fetchItemCount = async ({
    partId,
    quantity,
  }: PartIdCount): Promise<SparePartCount> => ({
    item: await sparePartService.fetchSparePartId(partId),
    quantity,
  });
  const reloadItems = async (items: PartIdCount[]): Promise<void> => {
    const newItems: SparePartCount[] = await Promise.all(
      items.map(fetchItemCount)
    );
    const countItemsObj: [ItemId, SparePartCount][] = newItems.map((obj) => [
      obj.item.id,
      obj,
    ]);
    setSparePartCounts(new Map(countItemsObj));
  };

  const createCart = (): void => {
    (async (): Promise<void> => {
      try {
        setIsLoading(true);
        const newCartId = await cartService.createCart();
        // TODO: Do we need to save cartId with user?
        Cookies.set('cartId', newCartId, {
          path: cookiePath,
          expires: 30,
        });
        setCartId(newCartId);
      } catch (err) {
        Cookies.remove('cartId', { path: cookiePath });
      } finally {
        setIsLoading(false);
      }
    })();
  };
  const loadCartItems = (): void => {
    (async (): Promise<void> => {
      try {
        setIsLoading(true);
        const cartItems = await cartService.getItems(cartId as string);
        await reloadItems(cartItems);
      } catch (err) {
        setCartId(null);
        throw err;
      } finally {
        setIsLoading(false);
      }
    })();
  };

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        if (cartId) {
          await loadCartItems();
        } else {
          await createCart();
        }
      } catch {
        // Ignore if it's from cart for now until hook approach
      }
    })();
  }, [cartId]);

  const getItems = (): SparePartCount[] => Array.from(sparePartCounts.values());

  const getItemCount = (id: ItemId): number => {
    const { quantity = 0 } = sparePartCounts.get(id) || {};
    return quantity;
  };
  const updateQuantities = (
    partCounts: PartIdCount[],
    newItem?: SparePart
  ): void => {
    setSparePartCounts((oldMap) => {
      const newObj = new Map();
      partCounts.forEach(({ partId, quantity }) => {
        if (oldMap.has(partId)) {
          newObj.set(partId, {
            item: ((oldMap.get(partId) || {}) as SparePartCount).item,
            quantity,
          });
        } else if (newItem && newItem.id === partId) {
          newObj.set(partId, {
            item: newItem,
            quantity,
          });
        }
      });
      return newObj;
    });
  };
  const editItem = async (
    item: SparePart,
    requestedQuantity: number
  ): Promise<void> => {
    const cartItems = await cartService.editItem(
      cartId as string,
      item,
      Math.min(requestedQuantity, CSS_CONFIG.ITEM_LIMIT)
    );
    updateQuantities(cartItems);
  };
  const addItem = async (
    item: SparePart,
    requestedAddQuantity = 1,
    undo = false,
    retry = 0
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const addQuantity = calculateNewNumberOfItemsWithUndo(
        getItemCount(item.id),
        requestedAddQuantity,
        CSS_CONFIG.ITEM_LIMIT,
        undo
      );
      const cartItems = await cartService.addToCart(
        cartId as string,
        item,
        addQuantity
      );
      reloadItems(cartItems);
      updateQuantities(cartItems, item);
    } catch (e) {
      if (cartDoesNotExist(e) && retry < 5) {
        await wait(retry * 1000);
        await createCart();
        await addItem(item, requestedAddQuantity, undo, retry + 1);
      } else {
        throw e;
      }
    } finally {
      setIsLoading(false);
    }
  };
  const removeItem = async (item: SparePart): Promise<void> => {
    await cartService.removeFromCart(cartId as string, item);
    const newObj = new Map(sparePartCounts.entries());
    newObj.delete(item.id);
    setSparePartCounts(newObj);
  };
  const createOrder = async (customer: CartRecipient): Promise<void> => {
    try {
      setIsLoading(true);
      if (!turnstileToken) {
        alert('Security validation failed. Please try again.');
        throw new Error('Turnstile token missing');
      }
      await cartService.orderCart(
        cartId as string,
        {
          ...customer,
          language: CSS_CONFIG.LANGUAGE,
          country: CSS_CONFIG.COUNTRY,
        },
        turnstileToken
      );
      Cookies.remove('cartId', { path: cookiePath });
    } finally {
      setIsLoading(false);
    }
  };

  const getTotalNrOfItems = (): number => {
    return Array.from(sparePartCounts.values()).reduce((a, b) => {
      return a + b.quantity;
    }, 0);
  };

  const value = {
    state: {
      items: sparePartCounts,
      recipient,
      isLoading,
    },
    getItemCount,
    getItems,
    editItem,
    addItem,
    removeItem,
    setRecipient,
    createOrder,
    getTotalNrOfItems,
    turnstileToken,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
const useCart = (): Cart => useContext(CartContext) as Cart;

export { CartContext, CartProvider, useCart };
